import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { TriangleAlert } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import TeamContext from 'app/frontend/base/config/context/team'
import {
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  EventPlanOverridesByPlanId,
  getActorOverrideCharacterId,
  getActorOverrideIgnored,
  getActorOverrideReplacementActor,
  getEncounterAssignmentAbilityHidden,
  getPlanActorOverrides,
  getPlanCastTargetOverrides,
  getPlanEncounterAssignmentOverrides,
} from './eventActivityPlanOverrides'
import {
  EventActivityAutoMatchMode,
  resolveEventActivityCastTargets,
  resolveEventActivityTimelineActors,
  resolveEventActivityTimelineAssignments,
  toResolvedParticipants,
} from './eventActivityTimelineResolver'
import {
  buildLinkedPlanAssignments,
  resolveLinkedPlanEncounterAssignmentRows,
  resolveLinkedPlanPhaseDividers,
  resolveLinkedPlanTimelineMaxTime,
} from '../hooks/resolveLinkedPlanEncounterAssignmentRows'
import { getEventActivityUnresolvedIssues } from '../hooks/applyEventActivityPlanOverrides'
import {
  getRaidlistParticipantCharacterId,
  getRaidlistParticipantId,
  ParsedRaidlistParticipant,
} from '../../TeamPlans/components/createNoteStorage'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { forceSelectedOnEntries } from '../../TeamPlans/components/default_setup/defaultSetupEntries'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import ResolvedPlanPreviewContent from './ResolvedPlanPreviewContent'
import { OverridePopoverLayerContext } from './EventPlanOverridePopover'
import {
  buildEventActivityLinkedPlanNoteParts,
  joinEventActivityLinkedPlanNote,
} from './eventActivityNoteExport'
import Card from 'app/frontend/base/components/Card'
import MarkerText from '../../TeamPlans/components/MarkerText'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { PlanAbilityDefaults } from '../../TeamPlans/components/planAbilityDefaults'

type Props = {
  owner: RaidPlanOwner
  raidPlan: RaidPlan
  planListPathPrefix: string
  parsedParticipants?: ParsedRaidlistParticipant[]
  rosterMemberships?: wowaudit.TeamMembership[]
  participantEntries?: EventActivityParticipantEntry[]
  autoMatchMode?: EventActivityAutoMatchMode
  planOverrides: EventPlanOverridesByPlanId
  stickyActorCharacterIdsByActorUuid?: Record<string, number>
  onResolvedActorIdentityMapChange?: (value: Record<string, string>) => void
  onResolvedNoteChange?: (value: string) => void
  displayMode?: 'visual' | 'export'
  onBack?: () => void
  onPlanOverridesChange: (value: EventPlanOverridesByPlanId) => void
  onActorPreferredCharacterChange?: (
    actorUuid: string,
    characterId: number,
  ) => void
  planAbilityDefaults?: PlanAbilityDefaults
}

const TIMELINE_COLUMN_ORDER = [
  'time',
  'ph',
  'bossSpell',
  'tag',
  'spellid',
  'text',
  'colors',
  'TTS',
  'glowunit',
]

const COMPACT_TIMELINE_COLUMNS = new Set(['time', 'ph', 'bossSpell', 'spellid'])

const parseTimedNoteLine = (line: string) => {
  const values: Record<string, string> = {}
  line
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const separatorIndex = part.indexOf(':')
      if (separatorIndex <= 0) return
      const key = part.slice(0, separatorIndex)
      const value = part.slice(separatorIndex + 1)
      if (!key) return
      values[key] = value
    })
  return values
}

const buildTimelineMatrix = (lines: string[]) => {
  const rows = lines.map(parseTimedNoteLine)
  const seenKeys = new Set<string>()
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => seenKeys.add(key))
  })
  const orderedKeys = TIMELINE_COLUMN_ORDER.filter((key) => seenKeys.has(key))
  const extraKeys = [...seenKeys].filter(
    (key) => !TIMELINE_COLUMN_ORDER.includes(key),
  )
  extraKeys.sort((left, right) => left.localeCompare(right))
  return {
    columns: [...orderedKeys, ...extraKeys],
    rows,
  }
}

const toCharacterRole = (role: string): CharacterRole => {
  if (role === 'tank') return 'tank'
  if (role === 'heal') return 'heal'
  if (role === 'ranged') return 'ranged'
  return 'melee'
}

const buildCharacterReference = (
  participant: ParsedRaidlistParticipant,
): wowaudit.CharacterReference => {
  const className = getClass(participant.classId.toString()) ?? 'Warrior'
  const specialization =
    participant.specId === null
      ? undefined
      : getSpecialization(participant.specId.toString())
  const role = toCharacterRole(specialization?.default_role ?? 'melee')
  return {
    id: getRaidlistParticipantCharacterId(participant.identity),
    name: participant.name,
    inGameName: participant.name,
    classId: participant.classId,
    characterClass: className ?? 'Warrior',
    role,
    currentSpecId: participant.specId ?? undefined,
    currentSpec: null,
    userId: getRaidlistParticipantId(participant.identity),
    realm: {
      name: participant.realm,
      slug: participant.realm.toLowerCase(),
      id: getRaidlistParticipantId(participant.identity),
      region: 'us',
      timezone: 'UTC',
    },
  } as unknown as wowaudit.CharacterReference
}

const buildTeamMembership = (
  participant: ParsedRaidlistParticipant,
): wowaudit.TeamMembership => {
  const specialization =
    participant.specId === null
      ? undefined
      : getSpecialization(participant.specId.toString())
  return {
    id: getRaidlistParticipantId(participant.identity),
    characterReference: buildCharacterReference(participant),
    role: toCharacterRole(specialization?.default_role ?? 'melee'),
    specId: participant.specId,
  } as wowaudit.TeamMembership
}

const buildParticipantEntries = (
  participants: ParsedRaidlistParticipant[],
  activityId: number,
) => {
  const teamMemberships = participants.map((participant) =>
    buildTeamMembership(participant),
  )
  const entries = teamMemberships.map<EventActivityParticipantEntry>(
    (membership) => {
      const participant = {
        id: membership.id,
        addedAsGuest: false,
        characterId: membership.characterReference.id,
        characterReference: membership.characterReference,
        effectiveDefaultSelectionStatus: true,
        role: membership.role,
        selections: {},
        specId: membership.specId,
        status: 'present',
        teamMembership: membership,
      } as unknown as wowaudit.EventParticipant
      return {
        participant,
        selected: true,
        role: membership.role,
        specId: membership.specId,
        characterId: membership.characterReference.id,
        availableTeamMemberships: teamMemberships,
        rosterCharacterById: Object.fromEntries(
          teamMemberships.map((entry) => [
            entry.characterReference.id,
            entry.characterReference,
          ]),
        ),
        selections: {
          [activityId]: {
            selected: true,
            role: membership.role,
            specId: membership.specId,
            characterId: membership.characterReference.id,
          },
        },
      }
    },
  )
  return { entries, teamMemberships }
}

const buildRosterParticipantEntries = (
  memberships: wowaudit.TeamMembership[],
  activityId: number,
) => {
  const entries = memberships.map<EventActivityParticipantEntry>(
    (membership) => {
      const participant = {
        id: membership.id,
        addedAsGuest: false,
        characterId: membership.characterReference.id,
        characterReference: membership.characterReference,
        effectiveDefaultSelectionStatus: true,
        role: membership.role,
        selections: {},
        specId: membership.specId,
        status: 'present',
        teamMembership: membership,
      } as unknown as wowaudit.EventParticipant
      return {
        participant,
        selected: true,
        role: membership.role,
        specId: membership.specId,
        characterId: membership.characterReference.id,
        availableTeamMemberships: memberships,
        rosterCharacterById: Object.fromEntries(
          memberships.map((entry) => [
            entry.characterReference.id,
            entry.characterReference,
          ]),
        ),
        selections: {
          [activityId]: {
            selected: true,
            role: membership.role,
            specId: membership.specId,
            characterId: membership.characterReference.id,
          },
        },
      }
    },
  )
  return { entries, teamMemberships: memberships }
}

const CreateNotePreview = ({
  owner,
  raidPlan,
  planListPathPrefix,
  parsedParticipants,
  rosterMemberships,
  participantEntries,
  autoMatchMode = 'full',
  planOverrides,
  stickyActorCharacterIdsByActorUuid,
  onResolvedActorIdentityMapChange,
  onResolvedNoteChange,
  displayMode = 'visual',
  onBack,
  onPlanOverridesChange,
  onActorPreferredCharacterChange,
  planAbilityDefaults,
}: Props) => {
  const { t } = useTranslation()
  const [exportView, setExportView] = useState<'parsed' | 'raw'>('parsed')
  const {
    data: { currentExpansion },
  } = usePageInfo()
  const activityId = raidPlan.id
  const encounter = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === raidPlan.encounterId),
    [currentExpansion, raidPlan.encounterId],
  )

  const { entries, teamMemberships } = useMemo(() => {
    if (participantEntries) {
      return {
        entries: participantEntries,
        teamMemberships: rosterMemberships ?? [],
      }
    }
    return rosterMemberships
      ? buildRosterParticipantEntries(rosterMemberships, activityId)
      : buildParticipantEntries(parsedParticipants ?? [], activityId)
  }, [activityId, parsedParticipants, participantEntries, rosterMemberships])
  const encounterRows = useMemo(
    () =>
      resolveLinkedPlanEncounterAssignmentRows({
        raidPlan,
        timeline: raidPlan.timeline,
        unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
      }),
    [raidPlan, t],
  )
  const phaseDividers = useMemo(
    () =>
      resolveLinkedPlanPhaseDividers({
        raidPlan,
        timeline: raidPlan.timeline,
      }),
    [raidPlan],
  )
  const linkedPlanAssignments = useMemo(
    () =>
      buildLinkedPlanAssignments({
        encounterAssignments: raidPlan.timeline.encounter_assignments,
        encounterRows,
      }),
    [encounterRows, raidPlan.timeline.encounter_assignments],
  )
  const selectedRows = useMemo(
    () => linkedPlanAssignments.map((entry) => entry.row),
    [linkedPlanAssignments],
  )
  const selectedParticipants = useMemo(
    () => toResolvedParticipants(entries, activityId),
    [activityId, entries],
  )
  const overrideParticipants = useMemo(
    () =>
      toResolvedParticipants(
        participantEntries
          ? forceSelectedOnEntries(participantEntries, activityId)
          : entries,
        activityId,
      ),
    [activityId, entries, participantEntries],
  )
  const actorOverrides = useMemo(
    () => getPlanActorOverrides(planOverrides, raidPlan.id),
    [planOverrides, raidPlan.id],
  )
  const encounterAssignmentOverrides = useMemo(
    () => getPlanEncounterAssignmentOverrides(planOverrides, raidPlan.id),
    [planOverrides, raidPlan.id],
  )
  const resolverResult = useMemo(
    () =>
      resolveEventActivityTimelineAssignments({
        activityId,
        entries,
        encounterRows: selectedRows,
        abilities: linkedPlanAssignments.map((entry) => entry.ability),
        autoMatchMode,
        spreadRoleCollections: false,
        encounterAssignmentOverrides,
        overrideParticipants,
      }),
    [
      activityId,
      autoMatchMode,
      encounterAssignmentOverrides,
      entries,
      linkedPlanAssignments,
      overrideParticipants,
      selectedRows,
    ],
  )
  const resolvedActors = useMemo(() => {
    const ignoredActorUuids: string[] = []
    const pinnedCharacterIdByActorUuid: Record<string, number> = {}
    const preferredCharacterIdByActorUuid: Record<string, number> = {}
    const effectiveActors = (raidPlan.timeline.actors ?? []).map((actor) => {
      const replacementActor = getActorOverrideReplacementActor({
        actorOverrides,
        actorUuid: actor.uuid,
      })
      const effectiveActor = replacementActor ?? actor
      if (getActorOverrideIgnored({ actorOverrides, actorUuid: actor.uuid })) {
        ignoredActorUuids.push(effectiveActor.uuid)
      }
      const overrideCharacterId = getActorOverrideCharacterId({
        actorOverrides,
        actorUuid: actor.uuid,
      })
      const replacementPreferredCharacterId =
        replacementActor?.preferred_character_id
      const pinnedCharacterId =
        typeof overrideCharacterId === 'number'
          ? overrideCharacterId
          : replacementPreferredCharacterId
      if (typeof pinnedCharacterId === 'number') {
        pinnedCharacterIdByActorUuid[effectiveActor.uuid] = pinnedCharacterId
      }
      const stickyCharacterId =
        stickyActorCharacterIdsByActorUuid?.[actor.uuid] ??
        stickyActorCharacterIdsByActorUuid?.[effectiveActor.uuid]
      if (typeof stickyCharacterId === 'number') {
        preferredCharacterIdByActorUuid[effectiveActor.uuid] = stickyCharacterId
      }
      return effectiveActor
    })
    const ignoredActorUuidSet = new Set(ignoredActorUuids)
    const actorResolverResult = resolveEventActivityTimelineActors({
      activityId,
      entries,
      actors: effectiveActors,
      autoMatchMode,
      overrideParticipants,
      pinnedCharacterIdByActorUuid,
      preferredCharacterIdByActorUuid,
      ignoredActorUuids,
    })
    return actorResolverResult.resolvedActors.map((resolvedActor) => ({
      ...resolvedActor,
      isIgnored: ignoredActorUuidSet.has(resolvedActor.actor.uuid),
    }))
  }, [
    activityId,
    actorOverrides,
    autoMatchMode,
    entries,
    overrideParticipants,
    raidPlan.timeline.actors,
    stickyActorCharacterIdsByActorUuid,
  ])
  const participantIdentityByCharacterId = useMemo(
    () =>
      Object.fromEntries(
        (parsedParticipants ?? []).map((participant) => [
          getRaidlistParticipantCharacterId(participant.identity),
          participant.identity,
        ]),
      ),
    [parsedParticipants],
  )
  const resolvedActorIdentityMap = useMemo(
    () =>
      resolvedActors.reduce<Record<string, string>>((acc, resolvedActor) => {
        if (!resolvedActor.participant) return acc
        const participantIdentity =
          participantIdentityByCharacterId[
            resolvedActor.participant.characterId
          ]
        if (!participantIdentity) return acc
        acc[resolvedActor.actor.uuid] = participantIdentity
        return acc
      }, {}),
    [participantIdentityByCharacterId, resolvedActors],
  )
  useEffect(() => {
    onResolvedActorIdentityMapChange?.(resolvedActorIdentityMap)
  }, [onResolvedActorIdentityMapChange, resolvedActorIdentityMap])
  const castTargetResolverResult = useMemo(
    () =>
      resolveEventActivityCastTargets({
        activityId,
        entries,
        actors: resolvedActors.map((resolvedActor) => resolvedActor.actor),
        generalCasts: raidPlan.timeline.general?.casts ?? [],
        castTargetOverrides: getPlanCastTargetOverrides(
          planOverrides,
          raidPlan.id,
        ),
        autoMatchMode,
        overrideParticipants,
      }),
    [
      activityId,
      autoMatchMode,
      entries,
      overrideParticipants,
      planOverrides,
      raidPlan.id,
      raidPlan.timeline.general?.casts,
      resolvedActors,
    ],
  )
  const { resolvedByScopeBoxKey } = resolverResult
  const sortedAbilityRows = useMemo(
    () =>
      linkedPlanAssignments
        .map((assignment) => ({
          abilityKey: assignment.abilityKey,
          row: assignment.row,
          ability: assignment.ability,
          isHidden: getEncounterAssignmentAbilityHidden({
            encounterAssignments: encounterAssignmentOverrides,
            abilityKey: assignment.abilityKey,
          }),
        }))
        .sort((left, right) => Number(left.isHidden) - Number(right.isHidden)),
    [encounterAssignmentOverrides, linkedPlanAssignments],
  )
  const hiddenAbilityKeys = useMemo(
    () =>
      new Set(
        sortedAbilityRows
          .filter((assignment) => assignment.isHidden)
          .map((assignment) => assignment.abilityKey),
      ),
    [sortedAbilityRows],
  )
  const unresolvedIssues = useMemo(
    () =>
      getEventActivityUnresolvedIssues(
        resolvedByScopeBoxKey,
        hiddenAbilityKeys,
      ),
    [hiddenAbilityKeys, resolvedByScopeBoxKey],
  )
  const unresolvedActorIssueCount = resolvedActors.filter(
    (resolvedActor) =>
      !resolvedActor.participant && resolvedActor.isIgnored !== true,
  ).length
  const linkedPlanIssueCount =
    unresolvedIssues.length +
    unresolvedActorIssueCount +
    castTargetResolverResult.issues.length
  const cooldownMaxTime = useMemo(
    () =>
      resolveLinkedPlanTimelineMaxTime({
        raidPlan,
        timeline: raidPlan.timeline,
      }),
    [raidPlan],
  )
  const isEmptyPreview =
    sortedAbilityRows.length === 0 && resolvedActors.length === 0
  const resolvedNoteParts = useMemo(() => {
    const noteActivity = {
      id: activityId,
      difficulty: raidPlan.difficulty,
      encounter,
    } as unknown as wowaudit.EventActivity
    return buildEventActivityLinkedPlanNoteParts({
      activity: noteActivity,
      linkedPlanData: {
        raidPlans: [
          {
            linkedRaidPlan: raidPlan,
            phaseDividers,
            linkedPlanAssignments,
            selectedRows,
            resolvedAssignmentAbilitiesByCharacterId: {},
            resolvedActors,
            resolverResult: {
              resolvedByScopeBoxKey,
              issues: unresolvedIssues,
            },
            actorResolverResult: {
              resolvedActors,
              issues: [],
            },
            castTargetResolverResult,
            linkedPlanIssueCount,
            hasLinkedPlanIssues: linkedPlanIssueCount > 0,
          },
        ],
        linkedVisualPlans: [],
        linkedPlans: [],
        selectedParticipants,
        linkedPlanIssueCount,
        hasLinkedPlanIssues: linkedPlanIssueCount > 0,
      },
      allEntries: entries,
      planAbilityDefaults,
    })
  }, [
    activityId,
    castTargetResolverResult,
    encounter,
    entries,
    linkedPlanAssignments,
    linkedPlanIssueCount,
    phaseDividers,
    raidPlan,
    resolvedActors,
    resolvedByScopeBoxKey,
    selectedParticipants,
    selectedRows,
    unresolvedIssues,
    planAbilityDefaults,
  ])
  const resolvedNote = useMemo(
    () => joinEventActivityLinkedPlanNote(resolvedNoteParts),
    [resolvedNoteParts],
  )
  const timelineMatrix = useMemo(
    () => buildTimelineMatrix(resolvedNoteParts.timedLines),
    [resolvedNoteParts.timedLines],
  )
  const timelineRawText = useMemo(
    () =>
      [
        resolvedNoteParts.header || null,
        resolvedNoteParts.header && resolvedNoteParts.timedLines.length > 0
          ? ''
          : null,
        ...resolvedNoteParts.timedLines,
      ]
        .filter((line) => line !== null)
        .join('\n'),
    [resolvedNoteParts.header, resolvedNoteParts.timedLines],
  )
  useEffect(() => {
    onResolvedNoteChange?.(resolvedNote)
  }, [onResolvedNoteChange, resolvedNote])

  return (
    <TeamContext.Provider
      value={{
        guild: {} as wowaudit.Guild,
        team: {
          id: owner.ownerType === 'team' ? owner.ownerId : 0,
          path: planListPathPrefix,
          members: teamMemberships,
          planAbilityDefaults: planAbilityDefaults ?? {},
        } as wowaudit.Team,
        refetchTeamDetails: () => null,
      }}
    >
      {linkedPlanIssueCount > 0 && displayMode !== 'export' ? (
        <Alert variant='warning' className='pb-2 mb-4'>
          <TriangleAlert className='h-4 w-4' />
          <AlertTitle>Issues to resolve: {linkedPlanIssueCount}</AlertTitle>
          <AlertDescription>
            You can resolve issues directly in this note by clicking on them.
            Unresolved entries will be omitted from the note when copied.
          </AlertDescription>
        </Alert>
      ) : null}
      {isEmptyPreview ? (
        <p className='text-sm text-muted-foreground text-center py-8'>
          This raid plan is empty
        </p>
      ) : displayMode === 'export' ? (
        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-2'>
            <Tabs
              value={exportView}
              onValueChange={(value) =>
                setExportView(value === 'raw' ? 'raw' : 'parsed')
              }
            >
              <TabsList>
                <TabsTrigger value='parsed'>
                  {t('dashboard.team.plans.create-note.preview-parsed')}
                </TabsTrigger>
                <TabsTrigger value='raw'>
                  {t('dashboard.team.plans.create-note.preview-raw')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {onBack ? (
              <Button type='button' variant='outline' onClick={onBack}>
                {t('dashboard.team.plans.create-note.back')}
              </Button>
            ) : null}
          </div>
          <div className='space-y-2'>
            <h4>
              {t('dashboard.team.plans.create-note.preview-assignments-title')}
            </h4>
            <p className='text-sm text-muted-foreground'>
              {t(
                'dashboard.team.plans.create-note.preview-assignments-description',
              )}
            </p>
            {resolvedNoteParts.summaryLines.length > 0 ? (
              <Card className='p-3'>
                <pre className='whitespace-pre-wrap text-sm font-mono'>
                  <MarkerText
                    text={resolvedNoteParts.summaryLines.join('\n')}
                  />
                </pre>
              </Card>
            ) : (
              <p className='text-sm text-muted-foreground'>
                {t('dashboard.team.plans.create-note.preview-empty')}
              </p>
            )}
          </div>
          <div className='space-y-2'>
            <h4>
              {t('dashboard.team.plans.create-note.preview-timeline-title')}
            </h4>
            <p className='text-sm text-muted-foreground'>
              {t(
                'dashboard.team.plans.create-note.preview-timeline-description',
              )}
            </p>
            {timelineRawText.trim().length > 0 ? (
              exportView === 'parsed' && timelineMatrix.columns.length > 0 ? (
                <div className='space-y-2'>
                  {resolvedNoteParts.header ? (
                    <Card className='p-3'>
                      <p className='font-mono text-xs'>
                        {resolvedNoteParts.header}
                      </p>
                    </Card>
                  ) : null}
                  <Card className='p-0 overflow-hidden'>
                    <table className='w-full border-collapse font-mono text-xs'>
                      <thead>
                        <tr>
                          {timelineMatrix.columns.map((column) => (
                            <th
                              key={column}
                              className={`border-r border-b border-border px-2 py-1.5 text-left font-medium text-muted-foreground last:border-r-0 ${
                                COMPACT_TIMELINE_COLUMNS.has(column)
                                  ? 'w-0 whitespace-nowrap'
                                  : ''
                              }`}
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {timelineMatrix.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className='last:[&>td]:border-b-0'>
                            {timelineMatrix.columns.map((column) => (
                              <td
                                key={column}
                                className={`border-r border-b border-border px-2 py-1.5 align-top last:border-r-0 ${
                                  COMPACT_TIMELINE_COLUMNS.has(column)
                                    ? 'w-0 whitespace-nowrap'
                                    : 'wrap-break-word whitespace-normal'
                                }`}
                              >
                                <MarkerText text={row[column] ?? ''} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </div>
              ) : (
                <Card className='p-3'>
                  <pre className='whitespace-pre-wrap text-sm font-mono'>
                    {timelineRawText}
                  </pre>
                </Card>
              )
            ) : (
              <p className='text-sm text-muted-foreground'>
                {t('dashboard.team.plans.create-note.preview-empty')}
              </p>
            )}
          </div>
        </div>
      ) : (
        <OverridePopoverLayerContext.Provider value={true}>
          <ResolvedPlanPreviewContent
            raidPlan={raidPlan}
            planListPathPrefix={planListPathPrefix}
            rosterMemberships={teamMemberships}
            planOverrides={planOverrides}
            onPlanOverridesChange={onPlanOverridesChange}
            sortedAbilityRows={sortedAbilityRows}
            selectedRows={selectedRows}
            phaseDividers={phaseDividers}
            selectedParticipants={selectedParticipants}
            overrideParticipants={overrideParticipants}
            resolvedByScopeBoxKey={resolvedByScopeBoxKey}
            resolvedActors={resolvedActors}
            resolvedCastTargetsByCastId={
              castTargetResolverResult.resolvedByCastId
            }
            cooldownMaxTime={cooldownMaxTime}
            canEdit={true}
            isSaving={false}
            onActorPreferredCharacterChange={onActorPreferredCharacterChange}
          />
        </OverridePopoverLayerContext.Provider>
      )}
    </TeamContext.Provider>
  )
}

export default CreateNotePreview
